import { parseChord } from './chord';
import type { Chord, Platform } from './keybindingTypes';

const ELECTRON_NAMED_KEYS: Readonly<Record<string, string>> = {
  Enter: 'Enter',
  Escape: 'Esc',
  Space: 'Space',
  Tab: 'Tab',
  Backspace: 'Backspace',
  Delete: 'Delete',
  Home: 'Home',
  End: 'End',
  PageUp: 'PageUp',
  PageDown: 'PageDown',
  ArrowLeft: 'Left',
  ArrowRight: 'Right',
  ArrowUp: 'Up',
  ArrowDown: 'Down',
};

const ELECTRON_NUMPAD_KEYS: Readonly<Record<string, string>> = {
  NumpadAdd: 'numadd',
  NumpadSubtract: 'numsub',
  NumpadMultiply: 'nummult',
  NumpadDivide: 'numdiv',
  NumpadDecimal: 'numdec',
};

function electronKey(chord: Chord, targetPlatform: Platform): string | null {
  if (chord.physical || chord.key === 'Pause') return null;
  if (/^[A-Z0-9]$/.test(chord.key)) return chord.key;
  // Electron accepts comma as a literal accelerator key. Keep Settings'
  // long-standing Mod+, native label/fallback while other punctuation stays
  // renderer-only because Electron interprets it through a US layout.
  if (chord.key === ',') return ',';
  if (/^F(?:[1-9]|1\d|2[0-4])$/.test(chord.key)) return chord.key;
  if (/^Numpad[0-9]$/.test(chord.key)) {
    return targetPlatform === 'mac' ? null : `num${chord.key.slice(-1)}`;
  }
  if (chord.key in ELECTRON_NUMPAD_KEYS) {
    return targetPlatform === 'mac' ? null : ELECTRON_NUMPAD_KEYS[chord.key];
  }
  return ELECTRON_NAMED_KEYS[chord.key] ?? null;
}

export function chordToElectronAccelerator(
  value: string | Chord,
  targetPlatform: Platform,
): string | null {
  const chord = typeof value === 'string' ? parseChord(value) : value;
  if (!chord || (targetPlatform !== 'mac' && chord.modifiers.includes('Cmd'))) return null;
  const key = electronKey(chord, targetPlatform);
  if (!key) return null;
  if (targetPlatform === 'mac' && chord.modifiers.includes('Alt') && /^[A-Z0-9]$/.test(chord.key)) {
    return null;
  }
  const modifiers = new Set<string>();
  for (const modifier of chord.modifiers) {
    if (modifier === 'Mod') modifiers.add(targetPlatform === 'mac' ? 'Command' : 'Control');
    else if (modifier === 'Cmd') modifiers.add('Command');
    else if (modifier === 'Ctrl') modifiers.add('Control');
    else modifiers.add(modifier);
  }
  return [...modifiers, key].join('+');
}
